import React, { useState, useEffect } from "react";
import { X, Plus, ExternalLink } from "lucide-react";
import {
  FetchWorkSchedulesData,
  inviteMember,
  AddManualEntryAPI,
} from "../../actioncreators/actioncreators";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import axios from "axios";
import { toast } from "react-toastify";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import { useSuspension } from "../../contexts/SuspensionContext";

const AddTimeEntry = ({ isOpen, onClose, onSuccess }) => {
  // Suspension check
  const { isSuspended } = useSuspension();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    employeeId: "",
    mobile: "",
    workScheduleId: "",
  });
  const [workSchedules, setWorkSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [activeTab, setActiveTab] = useState("manual");
  const [selectedOption, setSelectedOption] = useState("CHECKIN");
  const [optionDataList, setOptionDataList] = useState([
    {
      date: "",
      time: "",
      endTime: "",
      note: "",
      selectedOption: "CHECKIN",
    },
  ]);

  const getCurrentISTTime = () => {
    const now = new Date();
    const istOffset = 330; // IST offset in minutes (UTC +5:30)
    const utcTime = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
    const istTime = new Date(utcTime.getTime() + istOffset * 60000);
    return istTime.toTimeString().split(" ")[0].slice(0, 5);
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await FetchWorkSchedulesData(); // Fetch from your API
        console.log(response);

        // Assuming response.data has the workScheduleData
        const { businessStatusCode, viewListData } = response;

        // Check if the response contains the data as expected
        if (businessStatusCode === 2) {
          setWorkSchedules(viewListData); // Save work schedule data
          setOrganizationId(response?.viewListData?.[0]?.work_schedule_id);
        } else {
          throw new Error("Failed to fetch work schedules");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setOptionDataList((prev) =>
        prev.map((data) => ({
          ...data,
          time: getCurrentISTTime(),
          date: getTodayDate(),
        }))
      );
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const optionStyle = (isSelected) =>
    `col text-center py-2 ${
      isSelected
        ? "bg-red-100 text-red-600 border border-red-400"
        : "border-end"
    } cursor-pointer`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    // setError(null);

    try {
      // Call the inviteMember action creator with form data
      const response = await inviteMember({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobile: formData.mobile,
        workScheduleId: formData.workScheduleId || organizationId,
        employeeId: formData.employeeId || "", // Optional
      });

      // On success, trigger the onSuccess and close the modal
      onSuccess();
      onClose();
    } catch (err) {
      setError("Failed to send invite");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleOptionClick = (index, option) => {
    setOptionDataList((prev) => {
      const newData = [...prev];
      newData[index].selectedOption = option;
      return newData;
    });
  };

  const handleAddNew = () => {
    setOptionDataList((prev) => [
      ...prev,
      { date: "", time: "", endTime: "", note: "", selectedOption: "CHECKIN" },
    ]);
    setSelectedOption(""); // Reset selected option for new entry
  };

  const handleOptionDataChange = (index, e) => {
    const { name, value } = e.target;
    setOptionDataList((prev) => {
      const newData = [...prev];
      newData[index][name] = value;
      return newData;
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);

    const payload = {
      imageData: "",
      activityData: optionDataList.map((data) => ({
        activityType: data.selectedOption,
        activityTime: `${data.date} ${data.time}:00.000`,
        deviceDetails: {
          data: "Web",
        },
        reason: data.note,
      })),
    };

    try {
      const response = await AddManualEntryAPI(payload);
      if (response.businessStatusCode === 2) {
        toast.success(response.message || "Manual entry added successfully");
      } else {
        throw new Error(response.message || "Failed to save data");
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError("Failed to save data");
    } finally {
      setIsLoading(false);
    }
  };

  const isSaveDisabled = optionDataList.some(
    (data) =>
      !data.date ||
      !data.time ||
      (data.selectedOption === "BREAK" && !data.endTime) ||
      !data.note
  );

  return (
    <div
      className="row bg-white border p-0 m-0"
      style={{ overflowX: "hidden" }}
    >
      <div className="rounded-lg">
        {/* Header */}
        <div className="flex justify-between items-center mt-3 border-b">
          <h6 className="text-xl font-semibold">Add Manual Time Entry</h6>
          <button onClick={onClose} className="text-gray-400 text-black-600">
            <X size={24} />
          </button>
        </div>

        {/* Custom Tabs */}
        <div className="flex justify-start mb-3 border-b">
          <button
            style={{
              padding: "1rem 1rem",
              transition: "background-color 0.3s, color 0.3s",
              borderBottom:
                activeTab === "manual" ? "2px solid #fd5f07" : "none",
              marginLeft: "5px",
              borderRadius: "0px",
              color: activeTab === "manual" ? "#fd5f07" : "#000000",
            }}
            onClick={() => setActiveTab("manual")}
          >
            Time Entry
          </button>
          <button
            style={{
              padding: "1rem 1rem",
              transition: "background-color 0.3s, color 0.3s",
              borderBottom:
                activeTab === "Hourly Entry" ? "2px solid #fd5f07" : "none",
              marginLeft: "5px",
              borderRadius: "0px",
              color: activeTab === "Hourly Entry" ? "#fd5f07" : "#000000",
            }}
            onClick={() => setActiveTab("Hourly Entry")}
          >
            Hourly Entry
          </button>
        </div>

        {/* Content */}
        <div className="row">
          {activeTab === "manual" && (
            <div className="col-sm-12 col-md-12">
              {optionDataList.map((data, index) => (
                <React.Fragment key={index}>
                  <div className="col-sm-12 col-md-12">
                    <div className="d-flex border rounded">
                      {["CHECKIN", "BREAK", "CHECKOUT"].map((option) => (
                        <div
                          key={option}
                          className={optionStyle(
                            data.selectedOption === option
                          )}
                          onClick={() => handleOptionClick(index, option)}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                    {data.selectedOption && (
                      <div className="mt-4 ">
                        <div className="mb-4 row">
                          <div className="mb-3 col-6">
                            <input
                              type="time"
                              name="time"
                              value={data.time}
                              onChange={(e) => handleOptionDataChange(index, e)}
                              className="form-control"
                            />
                          </div>
                          {data.selectedOption === "BREAK" && (
                            <div className="mb-3 col-6">
                              <input
                                type="time"
                                name="endTime"
                                value={data.endTime}
                                onChange={(e) =>
                                  handleOptionDataChange(index, e)
                                }
                                className="form-control"
                              />
                            </div>
                          )}
                          <div className="mb-3 col-6">
                            <input
                              type="date"
                              name="date"
                              value={data.date}
                              onChange={(e) => handleOptionDataChange(index, e)}
                              className="form-control"
                            />
                          </div>

                          <div className="mb-3 col-12">
                            <input
                              type="text"
                              name="note"
                              placeholder="Add a note"
                              value={data.note}
                              onChange={(e) => handleOptionDataChange(index, e)}
                              className="form-control"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {index < optionDataList.length - 1 && <hr className="my-4" />}
                </React.Fragment>
              ))}
              <div
                onClick={handleAddNew}
                style={{ color: "#fd6c1b", cursor: "pointer" }}
              >
                +Add New
              </div>
            </div>
          )}
          {activeTab === "Hourly Entry" && (
            <div>
              <p>Hourly Entry functionality will be implemented here.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end mb-3 ">
          <ArthaCancelButton
            onClick={onClose}
            className="btn btn-secondary mr-2"
          >
            Cancel
          </ArthaCancelButton>
          <ArthaContinueButton
            onClick={isSuspended ? undefined : handleSave}
            className="btn btn-primary"
            disabled={isSaveDisabled || isSuspended}
            style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
            title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
          >
            Save
          </ArthaContinueButton>
        </div>
      </div>
    </div>
  );
};

export default AddTimeEntry;
